import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

const buttonVariants = cva(
  [
    "relative inline-flex items-center justify-center gap-2 select-none",
    "font-medium whitespace-nowrap rounded-lg",
    // Smooth motion across colour, border, shadow + a touch of transform on press
    "transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out",
    "active:translate-y-px active:scale-[0.98]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
    "disabled:pointer-events-none disabled:opacity-50",
    // Min touch target on mobile -- 44px height ensures fingers don't miss.
    "min-h-[44px] md:min-h-0",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-brand text-white hover:bg-brand-hover",
          "shadow-[0_1px_2px_rgb(249_115_22_/_0.25)] hover:shadow-[0_2px_6px_rgb(249_115_22_/_0.30)]",
        ],
        secondary: [
          "bg-surface text-text border border-border",
          "hover:border-border-strong hover:bg-surface-2",
        ],
        ghost: [
          "text-text-muted hover:text-text hover:bg-surface-2",
        ],
        outline: [
          "border border-border text-text hover:border-brand hover:text-brand",
        ],
        accent: [
          "bg-accent text-white hover:opacity-90",
          "shadow-[0_1px_2px_rgb(249_115_22_/_0.25)]",
        ],
        danger: [
          "bg-danger text-white hover:opacity-90 shadow-xs",
        ],
        link: [
          "text-brand hover:underline underline-offset-4 px-0 h-auto min-h-0",
        ],
      },
      size: {
        sm: "h-9 px-3 text-sm min-h-0",
        md: "h-10 px-4 text-sm",
        lg: "h-11 px-5 text-base",
        xl: "h-12 px-6 text-base",
        icon: "h-10 w-10 min-h-0",
      },
      fullWidth: {
        true: "w-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, asChild, loading, children, disabled, ...props }, ref) => {
    // Radix Slot enforces exactly one child via React.Children.only. When
    // asChild is on we MUST pass through `children` as-is and skip the loading
    // icon sibling.
    if (asChild) {
      return (
        <Slot
          ref={ref}
          className={cn(buttonVariants({ variant, size, fullWidth }), className)}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, fullWidth }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";
